import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Edit, FileText, GraduationCap, Shield, Upload, Download, Trash2, Camera, User, ShieldCheck, AlertTriangle, ClipboardList, History } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';

function StatusBadge({ status }) {
  const m = { valido: 'bg-green-50 text-green-700 border-green-200', in_scadenza: 'bg-amber-50 text-amber-700 border-amber-200', scaduto: 'bg-red-50 text-red-700 border-red-200' };
  const labels = { valido: 'Valido', in_scadenza: 'In Scadenza', scaduto: 'Scaduto' };
  return <Badge variant="outline" className={m[status] || m.valido}>{labels[status] || status}</Badge>;
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'anagrafica';
  const { isRole, user: currentUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [safetyPoints, setSafetyPoints] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [empReports, setEmpReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocForm, setShowDocForm] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showUploadDoc, setShowUploadDoc] = useState(null);
  const [showUploadCourse, setShowUploadCourse] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [docForm, setDocForm] = useState({ doc_type: 'attestato', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', is_mandatory: false });
  const [courseForm, setCourseForm] = useState({ course_name: '', course_type: '', training_entity: '', issue_date: '', expiry_date: '', certificate_number: '', is_mandatory: false });
  const [eligibility, setEligibility] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [courseTypes, setCourseTypes] = useState([]);
  const [courseSummary, setCourseSummary] = useState([]);
  const [courseHistory, setCourseHistory] = useState([]);
  const [renewCourse, setRenewCourse] = useState(null);
  const [renewForm, setRenewForm] = useState({ issue_date: '', training_entity: '', certificate_number: '' });
  const [showHistoryForm, setShowHistoryForm] = useState(false);
  const [historyForm, setHistoryForm] = useState({ course_name: '', course_type: '', training_entity: '', issue_date: '', certificate_number: '' });

  const typeInfo = (v) => courseTypes.find(t => t.value === v);
  const typeLabel = (v) => typeInfo(v)?.label || v || '-';
  const addYearsStr = (dateStr, yrs) => { if (!dateStr || !yrs) return ''; const d = new Date(dateStr); d.setFullYear(d.getFullYear() + yrs); return d.toISOString().slice(0, 10); };
  const previewExpiry = () => { const ti = typeInfo(courseForm.course_type); if (courseForm.expiry_date) return courseForm.expiry_date; if (ti?.years && courseForm.issue_date) return addYearsStr(courseForm.issue_date, ti.years); return ''; };

  const load = async () => {
    try {
      const [empRes, docsRes, coursesRes] = await Promise.all([
        api.get(`/employees/${id}`),
        api.get(`/employees/${id}/documents`),
        api.get(`/employees/${id}/courses`),
      ]);
      setEmp(empRes.data);
      setDocs(docsRes.data);
      setCourses(coursesRes.data);
      try {
        const [typesRes, sumRes, histRes] = await Promise.all([
          api.get('/employee-courses/types'),
          api.get(`/employees/${id}/courses/summary`),
          api.get(`/employees/${id}/course-history`),
        ]);
        setCourseTypes(typesRes.data);
        setCourseSummary(sumRes.data);
        setCourseHistory(histRes.data);
      } catch {}
      // Load safety points
      try {
        const [spRes, spHistRes] = await Promise.all([
          api.get(`/safety-points/employee/${id}`),
          api.get(`/safety-points/history/${id}`)
        ]);
        setSafetyPoints(spRes.data);
        setPointsHistory(spHistRes.data);
      } catch {}
      // Load employee reports
      try {
        const repRes = await api.get(`/employee-reports/by-employee/${id}`);
        setEmpReports(repRes.data);
      } catch {}
      try {
        const attRes = await api.get('/attendance', { params: { employee_id: id } });
        setAttendance(attRes.data.slice(0, 30));
      } catch {}
      try {
        const lvRes = await api.get('/leaves', { params: { employee_id: id } });
        setLeaves(lvRes.data);
      } catch {}
      try {
        const elRes = await api.get(`/employees/${id}/eligibility`);
        setEligibility(elRes.data);
      } catch {}
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleAddDoc = async () => {
    if (editingDoc) {
      await api.put(`/employee-documents/${editingDoc}`, docForm);
    } else {
      await api.post(`/employees/${id}/documents`, docForm);
    }
    setShowDocForm(false);
    setEditingDoc(null);
    setDocForm({ doc_type: 'attestato', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', is_mandatory: false });
    load();
  };

  const handleAddCourse = async () => {
    if (editingCourse) {
      await api.put(`/employee-courses/${editingCourse}`, courseForm);
    } else {
      await api.post(`/employees/${id}/courses`, courseForm);
    }
    setShowCourseForm(false);
    setEditingCourse(null);
    setCourseForm({ course_name: '', course_type: '', training_entity: '', issue_date: '', expiry_date: '', certificate_number: '', is_mandatory: false });
    load();
  };

  const openNewDoc = () => {
    setEditingDoc(null);
    setDocForm({ doc_type: 'attestato', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', is_mandatory: false });
    setShowDocForm(true);
  };

  const openEditDoc = (d) => {
    setEditingDoc(d.id);
    setDocForm({ doc_type: d.doc_type || 'attestato', title: d.title || '', reference_number: d.reference_number || '', issuing_entity: d.issuing_entity || '', issue_date: d.issue_date || '', expiry_date: d.expiry_date || '', is_mandatory: d.is_mandatory || false });
    setShowDocForm(true);
  };

  const openNewCourse = () => {
    setEditingCourse(null);
    setCourseForm({ course_name: '', course_type: '', training_entity: '', issue_date: '', expiry_date: '', certificate_number: '', is_mandatory: false });
    setShowCourseForm(true);
  };

  const openEditCourse = (c) => {
    setEditingCourse(c.id);
    setCourseForm({ course_name: c.course_name || '', course_type: c.course_type || '', training_entity: c.training_entity || '', issue_date: c.issue_date || '', expiry_date: c.expiry_date || '', certificate_number: c.certificate_number || '', is_mandatory: c.is_mandatory || false });
    setShowCourseForm(true);
  };

  const handleDownloadZip = async () => {
    try {
      const res = await api.get(`/employees/${id}/documents/zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      const cd = res.headers['content-disposition'] || '';
      a.download = cd.split('filename=')[1] || 'documenti.zip';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || 'Errore scaricamento ZIP'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try { await api.delete(`/employee-documents/${docId}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Eliminare questo corso?')) return;
    try { await api.delete(`/employee-courses/${courseId}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleUploadDoc = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try { await api.post(`/upload/employee-doc/${docId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); setShowUploadDoc(null); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore upload'); }
  };

  const handleUploadCourse = async (courseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try { await api.post(`/upload/employee-course/${courseId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); setShowUploadCourse(null); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore upload'); }
  };

  const openRenew = (c) => { setRenewForm({ issue_date: '', training_entity: c.training_entity || '', certificate_number: '' }); setRenewCourse(c); };
  const handleRenew = async () => {
    if (!renewCourse || !renewForm.issue_date) return;
    try { await api.post(`/employee-courses/${renewCourse.id}/renew`, renewForm); setRenewCourse(null); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore rinnovo'); }
  };
  const handleAddHistory = async () => {
    if (!historyForm.issue_date || (!historyForm.course_name && !historyForm.course_type)) { alert('Indica almeno tipo/nome corso e data'); return; }
    try { await api.post(`/employees/${id}/course-history`, historyForm); setShowHistoryForm(false); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };
  const handleDeleteHistory = async (hid) => { if (!window.confirm('Eliminare questa voce dello storico?')) return; try { await api.delete(`/employee-courses/history/${hid}`); load(); } catch (err) { alert('Errore'); } };

  const handleUploadProfilePhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/employees/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore durante il caricamento'); }
    finally { setUploadingPhoto(false); }
  };

  const handleDeleteProfilePhoto = async () => {
    if (!window.confirm('Eliminare la foto profilo?')) return;
    try { await api.delete(`/employees/${id}/photo`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const canEditPhoto = isRole('admin', 'aspp', 'qhse', 'capo_cantiere') || currentUser?.employee_id === id;

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 bg-slate-200 rounded w-48" /><div className="h-64 bg-slate-200 rounded" /></div>;
  if (!emp) return <div className="text-slate-500">Dipendente non trovato</div>;

  const eligMap = { idoneo: { l: 'Idoneo', c: 'bg-green-50 text-green-700 border-green-200' }, idoneo_con_criticita: { l: 'Criticita', c: 'bg-amber-50 text-amber-700 border-amber-200' }, non_idoneo: { l: 'Non Idoneo', c: 'bg-red-50 text-red-700 border-red-200' } };
  const es = eligMap[emp.eligibility_status] || eligMap.idoneo;

  // Safety points helpers
  const getPointsColor = () => {
    if (!safetyPoints) return 'text-green-600';
    if (safetyPoints.status === 'rosso') return 'text-red-600';
    if (safetyPoints.status === 'giallo') return 'text-amber-600';
    return 'text-green-600';
  };
  const getPointsBg = () => {
    if (!safetyPoints) return 'bg-green-50 border-green-200';
    if (safetyPoints.status === 'rosso') return 'bg-red-50 border-red-300';
    if (safetyPoints.status === 'giallo') return 'bg-amber-50 border-amber-300';
    return 'bg-green-50 border-green-200';
  };
  const maxPts = safetyPoints?.punti_iniziali ?? 20;
  const currentPts = safetyPoints?.current_points ?? maxPts;
  const pctPts = Math.max(0, Math.min(100, (currentPts / maxPts) * 100));

  const severityBadge = (sev) => {
    const colors = { bassa: 'bg-blue-100 text-blue-800', media: 'bg-amber-100 text-amber-800', alta: 'bg-orange-100 text-orange-800', critica: 'bg-red-100 text-red-800' };
    return <Badge className={colors[sev] || 'bg-slate-100'}>{sev}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
        
        {/* Profile Photo */}
        <div className="relative group">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center border-2 border-slate-300">
            {emp.profile_photo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${emp.profile_photo_url}`} alt={`${emp.first_name} ${emp.last_name}`} className="w-full h-full object-cover" />
            ) : (
              <User size={32} className="text-slate-400" />
            )}
          </div>
          {canEditPhoto && (
            <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <label className="cursor-pointer p-2">
                <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={(e) => e.target.files?.[0] && handleUploadProfilePhoto(e.target.files[0])} disabled={uploadingPhoto} />
                <Camera size={18} className="text-white" />
              </label>
              {emp.profile_photo_url && (
                <button onClick={handleDeleteProfilePhoto} className="p-2 text-white hover:text-red-300"><Trash2 size={16} /></button>
              )}
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{emp.first_name} {emp.last_name}</h1>
          <p className="text-sm text-slate-500">{emp.role_title} - {emp.department}</p>
        </div>
        <Badge variant="outline" className={es.c}><Shield size={12} className="mr-1" />{es.l}</Badge>
        {emp.is_active ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Attivo</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-500">Inattivo</Badge>}
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-slate-100 flex-wrap">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti ({docs.length})</TabsTrigger>
          <TabsTrigger value="corsi" data-testid="tab-corsi">Corsi ({courses.length})</TabsTrigger>
          <TabsTrigger value="punti" data-testid="tab-punti" className="flex items-center gap-1">
            <ShieldCheck size={14} />Punti ({currentPts}/{maxPts})
          </TabsTrigger>
          <TabsTrigger value="segnalazioni" data-testid="tab-segnalazioni" className="flex items-center gap-1">
            <ClipboardList size={14} />Segnalazioni ({empReports.length})
          </TabsTrigger>
          <TabsTrigger value="presenze" data-testid="tab-presenze">Presenze</TabsTrigger>
          <TabsTrigger value="ferie" data-testid="tab-ferie">Ferie/Permessi</TabsTrigger>
        </TabsList>

        {/* Anagrafica Tab */}
        <TabsContent value="anagrafica">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Dati Personali</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-slate-500">Codice Fiscale</span><span className="text-sm font-medium">{emp.fiscal_code || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Data di Nascita</span><span className="text-sm font-medium">{formatDate(emp.date_of_birth)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Telefono</span><span className="text-sm font-medium">{emp.phone || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Email</span><span className="text-sm font-medium">{emp.email || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Indirizzo</span><span className="text-sm font-medium">{emp.address || '-'}</span></div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Info Lavoro</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-slate-500">Mansione</span><span className="text-sm font-medium">{emp.role_title || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Reparto</span><span className="text-sm font-medium">{emp.department || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Livello</span><span className="text-sm font-medium">{emp.contract_level || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Costo Orario</span><span className="text-sm font-medium">{emp.hourly_cost ? `${emp.hourly_cost.toFixed(2)} EUR/h` : '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Data Assunzione</span><span className="text-sm font-medium">{formatDate(emp.hire_date)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Cantiere</span><span className="text-sm font-medium">{emp.current_site_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Disp. Notturno</span><span className="text-sm font-medium">{emp.night_availability ? 'Si' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Disp. Weekend</span><span className="text-sm font-medium">{emp.weekend_availability ? 'Si' : 'No'}</span></div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200 shadow-sm mt-4" data-testid="anagrafica-taglie">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Taglie Abbigliamento</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex justify-between"><span className="text-sm text-slate-500">Giubbotto</span><span className="text-sm font-medium" data-testid="detail-size-giubbotto">{emp.size_giubbotto || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Giacca</span><span className="text-sm font-medium" data-testid="detail-size-giacca">{emp.size_giacca || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Felpa</span><span className="text-sm font-medium" data-testid="detail-size-felpa">{emp.size_felpa || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">T-shirt</span><span className="text-sm font-medium" data-testid="detail-size-tshirt">{emp.size_tshirt || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Camicia</span><span className="text-sm font-medium" data-testid="detail-size-camicia">{emp.size_camicia || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Pantalone</span><span className="text-sm font-medium" data-testid="detail-size-pantalone">{emp.size_pantalone || '-'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Scarpe</span><span className="text-sm font-medium" data-testid="detail-size-scarpe">{emp.size_scarpe || '-'}</span></div>
            </CardContent>
          </Card>

          {/* Riepilogo Punti Sicurezza nella Anagrafica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card className={`border shadow-sm ${getPointsBg()}`} data-testid="anagrafica-safety-points">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck size={16} />Patente a Punti Sicurezza
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className={`text-3xl font-bold ${getPointsColor()}`}>{currentPts}</p>
                    <p className="text-xs text-slate-500">/ {maxPts} punti</p>
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                      <div className={`h-3 rounded-full transition-all ${safetyPoints?.status === 'rosso' ? 'bg-red-500' : safetyPoints?.status === 'giallo' ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pctPts}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Soglia idoneita: 12 pt</span>
                      {safetyPoints?.status === 'rosso' ? (
                        <Badge className="bg-red-600 text-white text-xs">NON IDONEO</Badge>
                      ) : safetyPoints?.status === 'giallo' ? (
                        <Badge className="bg-amber-500 text-white text-xs">ATTENZIONE</Badge>
                      ) : (
                        <Badge className="bg-green-600 text-white text-xs">IDONEO</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm" data-testid="anagrafica-reports-summary">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ClipboardList size={16} />Riepilogo Segnalazioni
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{empReports.length}</p>
                    <p className="text-xs text-slate-500">Totale</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{empReports.filter(r => r.severity === 'critica').length}</p>
                    <p className="text-xs text-slate-500">Critiche</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{empReports.filter(r => r.severity === 'alta').length}</p>
                    <p className="text-xs text-slate-500">Alte</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{empReports.filter(r => r.severity === 'media').length}</p>
                    <p className="text-xs text-slate-500">Medie</p>
                  </div>
                </div>
                {empReports.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">Ultima segnalazione: {formatDate(empReports[0]?.report_date)}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {eligibility && eligibility.issues?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 mt-4">
              <CardContent className="p-4">
                <p className="text-sm font-semibold text-amber-800 mb-2">Criticita Idoneita</p>
                {eligibility.issues.map((issue, i) => (
                  <p key={i} className="text-xs text-amber-700">- {issue.type}: {issue.item}</p>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Documenti Tab */}
        <TabsContent value="documenti">
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              {isRole('admin', 'aspp', 'qhse') && (
                <Button onClick={openNewDoc} data-testid="add-doc-btn"><Plus size={16} className="mr-1" /> Nuovo Documento</Button>
              )}
              {(docs.some(d => d.file_url) || courses.some(c => c.file_url)) && (
                <Button variant="outline" onClick={handleDownloadZip} data-testid="download-docs-zip-btn"><Download size={16} className="mr-1" /> Scarica ZIP (documenti + corsi)</Button>
              )}
            </div>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead><TableHead>Titolo</TableHead><TableHead className="hidden sm:table-cell">Ente</TableHead>
                      <TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead>File</TableHead>
                      {isRole('admin', 'aspp', 'qhse') && <TableHead className="w-20">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Nessun documento</TableCell></TableRow>
                    ) : docs.map(doc => (
                      <TableRow key={doc.id}>
                        <TableCell className="text-sm">{doc.doc_type}</TableCell>
                        <TableCell className="text-sm font-medium">{doc.title}</TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-slate-600">{doc.issuing_entity || '-'}</TableCell>
                        <TableCell className="text-sm">{formatDate(doc.expiry_date)}</TableCell>
                        <TableCell><StatusBadge status={doc.status} /></TableCell>
                        <TableCell>
                          {doc.file_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"><Download size={14} /> Scarica</a>
                          ) : doc.doc_type === 'busta_paga' ? (
                            <button type="button" onClick={() => alert('Non hai il permesso di scaricare le buste paga.')} className="text-slate-400 hover:text-slate-500 text-xs flex items-center gap-1 cursor-not-allowed" title="Non hai il permesso di scaricare le buste paga"><Download size={14} /> Scarica</button>
                          ) : isRole('admin', 'aspp', 'qhse') ? (
                            <Button variant="ghost" size="sm" onClick={() => setShowUploadDoc(doc.id)} className="text-xs p-1 h-auto"><Upload size={14} className="mr-1" /> Carica</Button>
                          ) : <span className="text-slate-400 text-xs">-</span>}
                        </TableCell>
                        {isRole('admin', 'aspp', 'qhse') && (
                          <TableCell>
                            <div className="flex gap-1">
                              {!doc.file_url && <Button variant="ghost" size="sm" onClick={() => setShowUploadDoc(doc.id)} className="p-1 h-auto text-blue-600"><Upload size={14} /></Button>}
                              <Button variant="ghost" size="sm" onClick={() => openEditDoc(doc)} className="p-1 h-auto text-slate-600 hover:text-slate-900" data-testid={`edit-doc-${doc.id}`}><Edit size={14} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="p-1 h-auto text-red-600 hover:text-red-800"><Trash2 size={14} /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Corsi Tab */}
        <TabsContent value="corsi">
          <div className="mt-4 space-y-4">
            {isRole('admin', 'aspp', 'qhse') && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={openNewCourse} data-testid="add-course-btn"><Plus size={16} className="mr-1" /> Nuovo Corso</Button>
                <Button variant="outline" onClick={() => { setHistoryForm({ course_name: '', course_type: '', training_entity: '', issue_date: '', certificate_number: '' }); setShowHistoryForm(true); }} data-testid="add-history-btn"><History size={16} className="mr-1" /> Aggiungi vecchio attestato</Button>
              </div>
            )}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><GraduationCap size={16} /> Riepilogo Corsi</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Corso</TableHead>
                      <TableHead className="hidden md:table-cell">Ultimo conseguimento</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="hidden sm:table-cell">Prima formazione</TableHead>
                      {isRole('admin', 'aspp', 'qhse') && <TableHead>File</TableHead>}
                      {isRole('admin', 'aspp', 'qhse') && <TableHead className="w-28">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courses.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Nessun corso</TableCell></TableRow>
                    ) : courses.map(c => {
                      const s = courseSummary.find(x => x.id === c.id) || {};
                      return (
                      <TableRow key={c.id} data-testid={`course-row-${c.id}`}>
                        <TableCell><div><p className="text-sm font-medium">{c.course_name}</p><p className="text-xs text-slate-500">{typeLabel(c.course_type)}</p></div></TableCell>
                        <TableCell className="hidden md:table-cell text-sm">{formatDate(c.issue_date)}</TableCell>
                        <TableCell className="text-sm">{formatDate(c.expiry_date)}</TableCell>
                        <TableCell><StatusBadge status={c.status} /></TableCell>
                        <TableCell className="hidden sm:table-cell text-sm">{s.first_training_date ? <span className="text-slate-700">{formatDate(s.first_training_date)}{s.history_count > 0 && <span className="text-xs text-slate-400"> · {s.history_count} in storico</span>}</span> : <span className="text-slate-400">-</span>}</TableCell>
                        {isRole('admin', 'aspp', 'qhse') && (
                          <TableCell>
                            {c.file_url ? (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${c.file_url}`} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-xs font-medium border border-blue-200" data-testid={`download-course-${c.id}`}><Download size={14} /> Scarica</a>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => setShowUploadCourse(c.id)} className="text-xs p-1 h-auto"><Upload size={14} className="mr-1" /> Carica</Button>
                            )}
                          </TableCell>
                        )}
                        {isRole('admin', 'aspp', 'qhse') && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openRenew(c)} className="p-1 h-auto text-green-600 hover:text-green-800" title="Rinnova (archivia il vecchio nello storico)" data-testid={`renew-course-${c.id}`}><History size={14} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditCourse(c)} className="p-1 h-auto text-slate-600 hover:text-slate-900" data-testid={`edit-course-${c.id}`}><Edit size={14} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCourse(c.id)} className="p-1 h-auto text-red-600 hover:text-red-800"><Trash2 size={14} /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Storico Formazione */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><History size={16} /> Storico Formazione ({courseHistory.length})</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Corso</TableHead><TableHead>Rilascio</TableHead><TableHead className="hidden sm:table-cell">Scadenza</TableHead><TableHead className="hidden sm:table-cell">Ente</TableHead><TableHead>File</TableHead>{isRole('admin', 'aspp', 'qhse') && <TableHead className="w-12"></TableHead>}</TableRow></TableHeader>
                  <TableBody>
                    {courseHistory.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Nessuno storico</TableCell></TableRow>
                    ) : courseHistory.map(h => (
                      <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                        <TableCell><div><p className="text-sm font-medium">{h.course_name || typeLabel(h.course_type)}</p><p className="text-xs text-slate-500">{typeLabel(h.course_type)}</p></div></TableCell>
                        <TableCell className="text-sm">{formatDate(h.issue_date)}</TableCell>
                        <TableCell className="hidden sm:table-cell text-sm">{formatDate(h.expiry_date)}</TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-slate-600">{h.training_entity || '-'}</TableCell>
                        <TableCell>{h.file_url ? <a href={`${process.env.REACT_APP_BACKEND_URL}${h.file_url}`} target="_blank" rel="noopener noreferrer" download className="text-blue-600 text-xs inline-flex items-center gap-1"><Download size={13} /> Scarica</a> : <span className="text-slate-400 text-xs">-</span>}</TableCell>
                        {isRole('admin', 'aspp', 'qhse') && <TableCell><Button variant="ghost" size="sm" onClick={() => handleDeleteHistory(h.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Punti Sicurezza Tab */}
        <TabsContent value="punti">
          <div className="mt-4 space-y-4">
            <Card className={`border shadow-sm ${getPointsBg()}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className={`text-5xl font-bold ${getPointsColor()}`}>{currentPts}</p>
                    <p className="text-sm text-slate-500 mt-1">/ 30 punti</p>
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-slate-200 rounded-full h-4 mb-3">
                      <div className={`h-4 rounded-full transition-all ${safetyPoints?.status === 'rosso' ? 'bg-red-500' : safetyPoints?.status === 'giallo' ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pctPts}%` }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Soglia minima idoneita: <strong>12 punti</strong></span>
                      {safetyPoints?.status === 'rosso' ? (
                        <Badge className="bg-red-600 text-white">NON IDONEO</Badge>
                      ) : safetyPoints?.status === 'giallo' ? (
                        <Badge className="bg-amber-500 text-white">ATTENZIONE</Badge>
                      ) : (
                        <Badge className="bg-green-600 text-white">IDONEO</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2"><History size={16} /> Storico Modifiche Punti</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Variazione</TableHead><TableHead>Motivazione</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pointsHistory.length === 0 ? (
                      <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-500">Nessuna modifica registrata</TableCell></TableRow>
                    ) : pointsHistory.map(h => (
                      <TableRow key={h.id}>
                        <TableCell className="text-sm">{formatDate(h.created_at)}</TableCell>
                        <TableCell>
                          <span className={`font-bold ${h.points_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {h.points_change > 0 ? '+' : ''}{h.points_change}
                          </span>
                          <span className="text-xs text-slate-400 ml-1">({h.points_before} &rarr; {h.points_after})</span>
                        </TableCell>
                        <TableCell className="text-sm">{h.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Segnalazioni Tab */}
        <TabsContent value="segnalazioni">
          <div className="mt-4 space-y-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2"><ClipboardList size={16} /> Segnalazioni del Dipendente</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Categoria</TableHead><TableHead>Gravita</TableHead>
                    <TableHead>Descrizione</TableHead><TableHead>Provvedimento</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {empReports.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Nessuna segnalazione</TableCell></TableRow>
                    ) : empReports.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm">{formatDate(r.report_date)}</TableCell>
                        <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                        <TableCell>{severityBadge(r.severity)}</TableCell>
                        <TableCell className="text-sm max-w-[300px]">{r.description}</TableCell>
                        <TableCell className="text-sm text-slate-500">{r.action_taken || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Presenze Tab */}
        <TabsContent value="presenze">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Entrata</TableHead><TableHead>Uscita</TableHead><TableHead>Ore</TableHead></TableRow></TableHeader>
                <TableBody>
                  {attendance.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Nessuna presenza registrata</TableCell></TableRow>
                  ) : attendance.map(a => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm">{a.date}</TableCell>
                      <TableCell className="text-sm">{a.clock_in ? new Date(a.clock_in).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-sm">{a.clock_out ? new Date(a.clock_out).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-sm">{a.hours_worked ? `${a.hours_worked}h` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ferie Tab */}
        <TabsContent value="ferie">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Periodo</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                <TableBody>
                  {leaves.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-500">Nessuna richiesta</TableCell></TableRow>
                  ) : leaves.map(l => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm capitalize">{l.request_type}</TableCell>
                      <TableCell className="text-sm">{formatDate(l.start_date)} - {formatDate(l.end_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          l.status === 'approvata' ? 'bg-green-50 text-green-700 border-green-200' :
                          l.status === 'rifiutata' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'
                        }>{l.status === 'in_attesa' ? 'In Attesa' : l.status === 'approvata' ? 'Approvata' : 'Rifiutata'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Document Dialog */}
      <Dialog open={showDocForm} onOpenChange={(o) => { setShowDocForm(o); if (!o) setEditingDoc(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingDoc ? 'Modifica Documento' : 'Nuovo Documento'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Tipo</Label>
              <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attestato">Attestato</SelectItem>
                  <SelectItem value="documento_identita">Documento Identita</SelectItem>
                  <SelectItem value="idoneita">Idoneita</SelectItem>
                  <SelectItem value="patentino">Patentino</SelectItem>
                  <SelectItem value="qualifica">Qualifica</SelectItem>
                  <SelectItem value="visita_medica">Visita Medica</SelectItem>
                  <SelectItem value="abilitazione">Abilitazione</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Titolo *</Label><Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} data-testid="doc-title" /></div>
            <div className="space-y-1.5"><Label>Numero Rif.</Label><Input value={docForm.reference_number} onChange={(e) => setDocForm({ ...docForm, reference_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ente Emittente</Label><Input value={docForm.issuing_entity} onChange={(e) => setDocForm({ ...docForm, issuing_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Rilascio</Label><Input type="date" value={docForm.issue_date} onChange={(e) => setDocForm({ ...docForm, issue_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Scadenza</Label><Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDocForm(false); setEditingDoc(null); }}>Annulla</Button>
            <Button onClick={handleAddDoc} disabled={!docForm.title} data-testid="doc-save-btn">{editingDoc ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Course Dialog */}
      <Dialog open={showCourseForm} onOpenChange={(o) => { setShowCourseForm(o); if (!o) setEditingCourse(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCourse ? 'Modifica Corso' : 'Nuovo Corso'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome Corso *</Label><Input value={courseForm.course_name} onChange={(e) => setCourseForm({ ...courseForm, course_name: e.target.value })} data-testid="course-name" /></div>
            <div className="space-y-1.5"><Label>Tipo Corso</Label>
              <Select value={courseForm.course_type || undefined} onValueChange={(v) => setCourseForm({ ...courseForm, course_type: v, expiry_date: '' })}>
                <SelectTrigger data-testid="course-type"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                <SelectContent>{courseTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}{t.years ? ` (${t.years} anni)` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ente Formatore</Label><Input value={courseForm.training_entity} onChange={(e) => setCourseForm({ ...courseForm, training_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nr. Certificato</Label><Input value={courseForm.certificate_number} onChange={(e) => setCourseForm({ ...courseForm, certificate_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Rilascio</Label><Input type="date" value={courseForm.issue_date} onChange={(e) => setCourseForm({ ...courseForm, issue_date: e.target.value })} data-testid="course-issue" /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Data Scadenza {typeInfo(courseForm.course_type)?.years ? '(automatica)' : ''}</Label>
              {typeInfo(courseForm.course_type)?.years ? (
                <>
                  <Input type="date" value={previewExpiry()} disabled data-testid="course-expiry" />
                  <p className="text-xs text-slate-500">Calcolata automaticamente: data rilascio + {typeInfo(courseForm.course_type).years} anni.</p>
                </>
              ) : (
                <Input type="date" value={courseForm.expiry_date} onChange={(e) => setCourseForm({ ...courseForm, expiry_date: e.target.value })} data-testid="course-expiry" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCourseForm(false); setEditingCourse(null); }}>Annulla</Button>
            <Button onClick={handleAddCourse} disabled={!courseForm.course_name} data-testid="course-save-btn">{editingCourse ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Course Dialog */}
      <Dialog open={!!renewCourse} onOpenChange={(o) => !o && setRenewCourse(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rinnova corso: {renewCourse?.course_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Il corso attuale (rilascio {formatDate(renewCourse?.issue_date)}) verrà archiviato nello Storico Formazione. La nuova scadenza sarà calcolata automaticamente in base al tipo.</p>
            <div className="space-y-1.5"><Label>Nuova data di conseguimento *</Label><Input type="date" value={renewForm.issue_date} onChange={(e) => setRenewForm({ ...renewForm, issue_date: e.target.value })} data-testid="renew-issue" /></div>
            {renewCourse && typeInfo(renewCourse.course_type)?.years && renewForm.issue_date && (
              <p className="text-xs text-green-700">Nuova scadenza: {formatDate(addYearsStr(renewForm.issue_date, typeInfo(renewCourse.course_type).years))}</p>
            )}
            <div className="space-y-1.5"><Label>Ente Formatore</Label><Input value={renewForm.training_entity} onChange={(e) => setRenewForm({ ...renewForm, training_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nr. Certificato</Label><Input value={renewForm.certificate_number} onChange={(e) => setRenewForm({ ...renewForm, certificate_number: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewCourse(null)}>Annulla</Button>
            <Button onClick={handleRenew} disabled={!renewForm.issue_date} data-testid="renew-save-btn">Rinnova</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add History Dialog */}
      <Dialog open={showHistoryForm} onOpenChange={setShowHistoryForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Aggiungi vecchio attestato allo storico</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome Corso</Label><Input value={historyForm.course_name} onChange={(e) => setHistoryForm({ ...historyForm, course_name: e.target.value })} data-testid="history-name" /></div>
            <div className="space-y-1.5"><Label>Tipo Corso</Label>
              <Select value={historyForm.course_type || undefined} onValueChange={(v) => setHistoryForm({ ...historyForm, course_type: v })}>
                <SelectTrigger data-testid="history-type"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                <SelectContent>{courseTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ente Formatore</Label><Input value={historyForm.training_entity} onChange={(e) => setHistoryForm({ ...historyForm, training_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data conseguimento *</Label><Input type="date" value={historyForm.issue_date} onChange={(e) => setHistoryForm({ ...historyForm, issue_date: e.target.value })} data-testid="history-issue" /></div>
            <div className="space-y-1.5"><Label>Nr. Certificato</Label><Input value={historyForm.certificate_number} onChange={(e) => setHistoryForm({ ...historyForm, certificate_number: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryForm(false)}>Annulla</Button>
            <Button onClick={handleAddHistory} disabled={!historyForm.issue_date} data-testid="history-save-btn">Salva nello storico</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={!!showUploadDoc} onOpenChange={(o) => { if (!o) { setShowUploadDoc(null); setPendingUpload(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carica File Documento</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx" onChange={(e) => setPendingUpload(e.target.files?.[0] || null)} data-testid="upload-file-input" />
            <p className="text-xs text-slate-500">Formati accettati: PDF, DOC, DOCX, immagini, Excel</p>
            <FilePreview file={pendingUpload} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUploadDoc(null); setPendingUpload(null); }}>Annulla</Button>
            <Button onClick={() => { handleUploadDoc(showUploadDoc, pendingUpload); setPendingUpload(null); }} disabled={!pendingUpload} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Course Dialog */}
      <Dialog open={!!showUploadCourse} onOpenChange={(o) => { if (!o) { setShowUploadCourse(null); setPendingUpload(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carica Attestato Corso</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif" onChange={(e) => setPendingUpload(e.target.files?.[0] || null)} data-testid="upload-course-file-input" />
            <p className="text-xs text-slate-500">Formati accettati: PDF, DOC, DOCX, immagini</p>
            <FilePreview file={pendingUpload} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUploadCourse(null); setPendingUpload(null); }}>Annulla</Button>
            <Button onClick={() => { handleUploadCourse(showUploadCourse, pendingUpload); setPendingUpload(null); }} disabled={!pendingUpload} data-testid="upload-course-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
